import { Component, OnInit, OnDestroy, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { NgIf, NgFor, NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { SearchService } from '../search.service';
import { UnifiedSearchResult, SearchSource } from '../searchResult';

import {
  faBuilding, faFolderTree, faFolder, faCloud, faFile, faFilePdf, faFileWord, faFileExcel,
  faFilePowerpoint, faFileImage, faFileZipper, faFileCsv, faFileLines, faFileCode,
  faEnvelope, faUser, faSackDollar, faFileContract, faLayerGroup
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';

type Filter = string;

@Component({
  selector: 'cub-search-main',
  templateUrl: './search-main.component.html',
  styleUrls: ['./search-main.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgIf, NgFor, NgTemplateOutlet, MatListModule, MatIconModule, MatTooltipModule,
    MatCardModule, MatRippleModule, MatExpansionModule, FontAwesomeModule]
})
export class SearchMainComponent implements OnInit, OnDestroy {

  results: UnifiedSearchResult[] = [];
  sections: {
    key: string; label: string; count: number; img?: string; icon?: any; colorKey: string;
    results: UnifiedSearchResult[];
    leases?: { name: string; count: number; folders: { name: string; results: UnifiedSearchResult[] }[] }[];
  }[] = [];
  pills: { key: string; label: string; count: number; img?: string; icon?: any; colorKey: string }[] =
    [{ key: 'All', label: 'All', count: 0, icon: faLayerGroup, colorKey: 'All' }];
  isSearching = false;
  isRemote: boolean;
  activeFilter: Filter = 'All';
  // A facet filter requested via the URL (?filter=...) e.g. from the quick-search popup's category
  // rows. Applied once, after the first result set for a term arrives and the pills are built.
  private pendingFilter: string | null = null;
  sharePointError = '';
  spIcon = faCloud;
  folderIcon = faFolder;

  private subs: Subscription[] = [];

  constructor(
    @Inject(DOCUMENT) private document: any,
    private searchService: SearchService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {
    this.isRemote = this.document.location.href.startsWith('https');
  }

  ngOnInit() {
    // Re-run the unified search whenever the term (typed in the top-menu search box) changes.
    this.subs.push(
      this.searchService.term$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(term => {
          if (!term || term.length < 2) {
            this.results = [];
            this.isSearching = false;
            this.recompute();
            return [];
          }
          this.isSearching = true;
          return this.searchService.searchAll(term);
        })
      ).subscribe(results => {
        this.results = results;
        this.isSearching = false;
        this.recompute();
        this.applyPendingFilter();
      })
    );

    // Push the current term into the URL (?q=...) so the search survives a refresh / can be shared.
    // One-way + self-guarded (lastPushedQ) so it can't feed back into term$ and loop.
    let lastPushedQ = this.route.snapshot.queryParamMap.get('q') || '';
    this.subs.push(
      this.searchService.term$.pipe(distinctUntilChanged()).subscribe(term => {
        const t = term || '';
        if (t !== lastPushedQ) {
          lastPushedQ = t;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: t ? { q: t } : {},
            replaceUrl: true
          });
        }
      })
    );

    // Seed the search from the URL ONCE on load (refresh / deep link / "More Results") so results
    // appear without retyping. We read the snapshot instead of subscribing to queryParamMap to avoid
    // a navigate <-> setTerm feedback loop.
    this.pendingFilter = this.route.snapshot.queryParamMap.get('filter');
    const initialQ = this.route.snapshot.queryParamMap.get('q') || '';
    if (initialQ) {
      this.searchService.setTerm(initialQ);
    }

    this.subs.push(
      this.searchService.sharePointError$.subscribe(msg => this.sharePointError = msg)
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  // Recomputes the pill list + filtered results whenever the result set changes.
  // Done as plain fields (not template getters) so change detection doesn't re-run this on every
  // tick — a getter feeding mat-chip-listbox's *ngFor causes runaway CD that freezes the page.
  private recompute() {
    const present = new Set(this.results.map(r => r.group || r.source));
    const ordered: string[] = [];
    if (present.has('CUB')) { ordered.push('CUB'); present.delete('CUB'); }
    if (present.has('Egnyte')) { ordered.push('Egnyte'); present.delete('Egnyte'); }
    if (present.has('Property Files')) { ordered.push('Property Files'); present.delete('Property Files'); }
    if (present.has('OneDrive')) { ordered.push('OneDrive'); present.delete('OneDrive'); }
    if (present.has('Lease Files')) { ordered.push('Lease Files'); present.delete('Lease Files'); }
    ordered.push(...Array.from(present).sort((a, b) => a.localeCompare(b)));

    this.pills = [
      { key: 'All', label: 'All', count: this.results.length, icon: faLayerGroup, colorKey: 'All' },
      ...ordered.map(g => ({ key: g, label: g, count: this.countFor(g), ...this.pillIconMeta(g) }))
    ];

    // If the active filter's group disappeared after a new search, fall back to "All". The meta
    // "SharePoint" filter (all SharePoint groups except Lease Files) survives as long as any such
    // result is present, even though it isn't a single pill key.
    const activeStillValid = ordered.includes(this.activeFilter) ||
      (this.activeFilter === 'SharePoint' && this.hasSharePointMetaResults());
    if (this.activeFilter !== 'All' && !activeStillValid) {
      this.activeFilter = 'All';
    }
    this.applyFilter();
  }

  // Applies a filter requested via ?filter=... once results exist. Cleared after the first apply so
  // it doesn't override the user's later pill choices.
  private applyPendingFilter() {
    if (!this.pendingFilter) {
      return;
    }
    const want = this.pendingFilter;
    const valid = this.pills.some(p => p.key === want) ||
      (want === 'SharePoint' && this.hasSharePointMetaResults());
    if (valid) {
      this.activeFilter = want;
      this.applyFilter();
    }
    this.pendingFilter = null;
  }

  private hasSharePointMetaResults(): boolean {
    return this.results.some(r => r.source === 'SharePoint' && (r.group || r.source) !== 'Lease Files');
  }

  // Whether a group (pill key) should be shown for the current filter. Handles the meta
  // "SharePoint" filter, which selects every SharePoint-source group other than Lease Files.
  private groupMatchesActiveFilter(groupKey: string): boolean {
    if (this.activeFilter === 'All') {
      return true;
    }
    if (this.activeFilter === 'SharePoint') {
      return groupKey !== 'Lease Files' &&
        this.results.some(r => (r.group || r.source) === groupKey && r.source === 'SharePoint');
    }
    return groupKey === this.activeFilter;
  }

  private applyFilter() {
    // Build one collapsible section per group, honoring the active facet-rail filter. The meta
    // "SharePoint" filter keeps every SharePoint-source group except Lease Files (which has its
    // own facet), so it can span multiple pills (OneDrive, individual sites, ...).
    const keys = this.pills
      .filter(p => p.key !== 'All' && this.groupMatchesActiveFilter(p.key))
      .map(p => p.key);
    this.sections = keys.map(g => {
      const meta = this.pillIconMeta(g);
      let results = this.results.filter(r => (r.group || r.source) === g);
      const section: any = { key: g, label: g, colorKey: meta.colorKey, img: meta.img, icon: meta.icon };
      // Lease Files and Egnyte Property Files get a second level of grouping: property/lease name,
      // then folder name. Folder hits are redundant there (folders are represented by the group
      // headers), so show only files.
      if (g === 'Lease Files' || g === 'Property Files') {
        results = results.filter(r => r.typeHint !== 'folder');
        section.leases = this.buildLeaseGroups(results);
      }
      section.results = results;
      section.count = results.length;
      return section;
    }).filter(s => s.results.length > 0);
  }

  // Groups Lease Files results by lease name, then folder name (parsed from the path in the service).
  private buildLeaseGroups(results: UnifiedSearchResult[]) {
    const leaseMap = new Map<string, Map<string, UnifiedSearchResult[]>>();
    for (const r of results) {
      const lease = r.leaseName || 'Other';
      const folder = r.folderName || '';
      if (!leaseMap.has(lease)) { leaseMap.set(lease, new Map()); }
      const folders = leaseMap.get(lease)!;
      if (!folders.has(folder)) { folders.set(folder, []); }
      folders.get(folder)!.push(r);
    }
    return Array.from(leaseMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, folders]) => ({
        name,
        count: Array.from(folders.values()).reduce((n, arr) => n + arr.length, 0),
        folders: Array.from(folders.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([fname, rs]) => ({ name: fname, results: rs }))
      }));
  }

  private countFor(group: Filter): number {
    if (group === 'All') {
      return this.results.length;
    }
    return this.results.filter(r => (r.group || r.source) === group).length;
  }

  // Facet-rail icon per source. Uses the same brand logos as the top menu bar (CUB / SharePoint /
  // Egnyte are white logos, shown on a solid brand-colored circle). OneDrive keeps the cloud glyph.
  private pillIconMeta(key: string): { img?: string; icon?: any; colorKey: string } {
    switch (key) {
      case 'CUB': return { img: 'assets/img/logo-white-40w.png', colorKey: 'CUB' };
      case 'Egnyte': return { img: 'assets/img/egnyte-logo-white.png', colorKey: 'Egnyte' };
      case 'Property Files': return { img: 'assets/img/egnyte-logo-white.png', colorKey: 'Egnyte' };
      case 'OneDrive': return { icon: faCloud, colorKey: 'OneDrive' };
      default: return { img: 'assets/img/sharepoint-logo-white-100x.png', colorKey: 'SharePoint' };
    }
  }

  setFilter(filter: Filter) {
    this.activeFilter = filter;
    this.applyFilter();
  }

  sourceLabel(source: SearchSource): string {
    return source;
  }

  // Returns a brand-logo asset path when the row should show a source logo instead of a file/type
  // glyph. SharePoint list/site items aren't files, so we show the SharePoint logo for them.
  rowLogo(result: UnifiedSearchResult): string | null {
    if (result.source === 'SharePoint' && result.typeHint === 'listItem') {
      return 'assets/img/sharepoint-logo-white-100x.png';
    }
    return null;
  }

  // Leading icon: file-type aware for Egnyte/SharePoint, entity-type aware for CUB.
  iconFor(result: UnifiedSearchResult) {
    if (result.source === 'CUB') {
      switch ((result.typeHint || '').toLowerCase()) {
        case 'user': return faUser;
        case 'property': return faBuilding;
        case 'investment': return faSackDollar;
        case 'lease': return faFileContract;
        default: return faBuilding;
      }
    }
    if (result.typeHint === 'folder') {
      return faFolder;
    }
    return this.fileIcon(result.title);
  }

  private fileIcon(filename: string) {
    const ext = (filename || '').substring((filename || '').lastIndexOf('.') + 1).toLowerCase();
    switch (ext) {
      case 'pdf': return faFilePdf;
      case 'doc':
      case 'docx':
      case 'rtf': return faFileWord;
      case 'xls':
      case 'xlsx':
      case 'xlsm': return faFileExcel;
      case 'ppt':
      case 'pptx': return faFilePowerpoint;
      case 'csv': return faFileCsv;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'tif':
      case 'tiff': return faFileImage;
      case 'zip':
      case 'rar':
      case '7z': return faFileZipper;
      case 'msg':
      case 'eml': return faEnvelope;
      case 'txt': return faFileLines;
      case 'json':
      case 'xml':
      case 'html':
      case 'js':
      case 'ts':
      case 'cs': return faFileCode;
      default: return faFile;
    }
  }

  itemClick(item: UnifiedSearchResult) {
    this.searchService.closeSearch();
    switch (item.source) {
      case 'CUB':
        this.navigateCub(item.url);
        break;
      case 'Egnyte':
        window.open(environment.egnyteUrl + item.url.toLowerCase().replace('/shared/', ''));
        break;
      case 'SharePoint':
        if (item.url) {
          window.open(item.url);
        }
        break;
    }
  }

  // CUB links come in two shapes: relative SPA routes (e.g. "properties/101356/leases/1356fgps")
  // and absolute portal URLs from the table search (e.g. "https://cub.centerpoint.com/properties/101485").
  // Route in-app when the target is this SPA (relative, or absolute on our own origin); otherwise open
  // it in a new tab. Navigating must be absolute — relativeTo the /search route would prepend "/search/".
  private navigateCub(url: string) {
    const raw = (url || '').trim();
    if (!raw) {
      return;
    }
    // Investments come back as a legacy javascript link ("javascript:showEditInv(2563);") — map it
    // to the Angular investment detail route instead of opening it as a URL.
    const inv = raw.match(/showEditInv\((\d+)\)/i);
    if (inv) {
      this.router.navigateByUrl('/investments/' + inv[1]);
      return;
    }
    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsed = new URL(raw);
        if (parsed.origin === window.location.origin) {
          this.router.navigateByUrl(parsed.pathname + parsed.search + parsed.hash);
        } else {
          window.open(raw);
        }
      } catch {
        window.open(raw);
      }
      return;
    }
    this.router.navigateByUrl('/' + raw.replace(/^\//, ''));
  }
}
